import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { last, append, remove, findIndex, propEq, path, eqProps, isEmpty } from 'ramda';
import cx from 'classnames';
import Translate from 'react-translate-component';
import withLoad from 'components/hocs/withLoad';
import { Upload } from 'antd';

import Error from 'components/common/form/Error';
import { MAX_FILE_SIZE } from 'constants/events';
import { limits } from 'constants/dictionaries';
import CustomIcon from 'components/common/CustomIcon';

function arrayBufferToBase64(buffer) {
   let binary = '';
   const bytes = new Uint8Array(buffer);
   const len = bytes.byteLength;

   for (let i = 0; i < len; i++) {
       binary += String.fromCharCode(bytes[i]);
   }

   return window.btoa(binary);
}

@withLoad
class FileInput extends Component {
    static propTypes = {
        input: PropTypes.object,
        meta: PropTypes.object,
        label: PropTypes.node,
        accept: PropTypes.string,
        multipleDocuments: PropTypes.bool,
        documentType: PropTypes.string,
        loading: PropTypes.bool,
        getRef: PropTypes.func
    }

    state = {
        filename: '',
        fileList: [],
    }

    componentDidMount() {
        this.props.getRef && this.props.getRef(this);
    }

    componentDidUpdate(prevProps) {
        const { value } = prevProps.input;
        const nextValue = this.props.input.value;

        if (this.props.multipleDocuments) {
            if (this.state.fileList.length && value && (!nextValue || isEmpty(nextValue))) {
                this.setState({
                    fileList: []
                });
            }
        } else {
            if (!eqProps('name', value, nextValue)) {
                this.setState({
                    filename: nextValue.name || ''
                });
            }
        }
    }

    onRemove = file => {
        const { multipleDocuments, input: { value }} = this.props;

        if (multipleDocuments) {
            const index = findIndex(propEq('uid', file.uid), this.state.fileList);

            this.props.input.onChange(remove(index, 1, value));
            this.setState(prev => ({
                filename: '',
                fileList: remove(index, 1, prev.fileList),
            }));
        } else {
            this.props.input.onChange(null);
            this.setState({
                filename: '',
                fileList: []
            });
        }
    }

    getMaxFileSizeMB = () => path(['dictionaryAction', 'data', 'maxDocFileSize'], this.props);

    getMaxFileSize = () => {
        const maxDocFileSize = this.getMaxFileSizeMB();

        return maxDocFileSize ? maxDocFileSize * 1000000 : MAX_FILE_SIZE
    }

    onChange = (file) => {
        const { input: { onChange }, documentType, multipleDocuments } = this.props;

        return new Promise((resolve, reject) => {
            if (file) {
                const { size, name } = file;
                const maxFileSize = this.getMaxFileSize();
                const overSize = size > maxFileSize;

                if (overSize) {
                    const payload = {
                        uid: file.uid,
                        name,
                        documentType,
                        fileType: last(name.split('.')),
                        status: 'error',
                        size,
                    };

                    this.setState(prev => ({
                        filename: name,
                        fileList: multipleDocuments ? append(payload, prev.fileList) : [payload],
                    }), () => {
                        const { fileList } = this.state;
                        onChange(multipleDocuments ? fileList : payload);
                        reject();
                    });
                } else {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(file);
                    reader.onload = () => {
                        const { input: { value } } = this.props;
                        const payload = {
                            uid: file.uid,
                            name,
                            documentType,
                            fileType: last(name.split('.')),
                            body: arrayBufferToBase64(reader.result),
                            status: 'done',
                            size,
                        };

                        this.setState(prev => ({
                            filename: name,
                            fileList: multipleDocuments ? append(payload, prev.fileList) : [payload],
                        }), () => {
                            onChange(multipleDocuments ? append(payload, value || []) : payload);
                            reject();
                        });
                    };
                }
            }
        });
    }

    render() {
        const { label, accept, meta: { invalid, dirty, submitFailed }, meta, multipleDocuments, loading } = this.props;
        const { fileList } = this.state;
        const hasError = invalid && (submitFailed || dirty);

        return (
            <div>
                <div className={cx('form-group', { error: hasError })}>
                    <label>{label}</label>
                    <div className='file-upload-container'>
                        <Upload.Dragger
                            accept={accept}
                            multiple={multipleDocuments}
                            onRemove={this.onRemove}
                            beforeUpload={this.onChange}
                            fileList={fileList}
                            disabled={loading}
                        >
                            <p className="ant-upload-drag-icon">
                                <CustomIcon type="text-document" />
                            </p>
                            <p className="ant-upload-text">
                                <Translate content="placeholders.uploadText" component="span" />
                            </p>
                            <p className="ant-upload-hint">
                                <Translate content="placeholders.uploadHint" component="span" with={{ size: this.getMaxFileSizeMB() || 32 }} />
                            </p>
                        </Upload.Dragger>
                    </div>
                    { hasError && <Error events={['dirty']} defaultEvents={[]} {...meta} /> }
                </div>
            </div>
        );
    }
}

const FileInputWithLoad = (props) => <FileInput {...props} loadName={limits} />;

export default FileInputWithLoad;
